import { useCallback, useState } from 'react';
import { getMarketCode } from '../utils/market';

interface LeadMetaData {
  key: string;
  value: string;
}

interface LeadPayload {
  firstName: string;
  lastName: string;
  type: string;
  phoneNumber?: string;
  branchId: string;
  email?: string;
  metaData: LeadMetaData[];
}

interface LeadResponse {
  success: boolean;
  id?: string;
  message?: string;
}

interface SubmitLeadProps {
  itemId: string;
  branchId: string;
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  email?: string;
  message?: string;
  communication: 'callme' | 'email';
  tradeInCarRegNo?: string;
  tradeInCarMileage?: string;
}

const useSubmitLead = (apiUrl: string) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [responseData, setResponseData] = useState<LeadResponse | null>(null);

  const marketCode = getMarketCode();

  const submitLead = useCallback(
    async ({
      itemId,
      firstName,
      lastName,
      phoneNumber,
      email,
      message,
      tradeInCarRegNo,
      tradeInCarMileage,
      branchId,
      communication,
    }: SubmitLeadProps): Promise<LeadResponse> => {
      setIsLoading(true);
      setError(null);

      const payload: LeadPayload = {
        firstName,
        lastName,
        phoneNumber,
        email,
        branchId,
        type: 'registrationOfInterestToBuy',
        metaData: [
          { key: 'source', value: window.location.hostname.replace(/^www\./, '') },
          { key: 'sourceMechanism', value: `cta.${communication}` },
          { key: 'itemForSaleId', value: itemId },
          ...(tradeInCarRegNo ? [{ key: 'registrationNumber', value: tradeInCarRegNo }] : []),
          // readingUnit describes the unit of tradeInCarMileage, so it only travels with it.
          ...(tradeInCarMileage
            ? [
                { key: 'tradeInCarMileage', value: tradeInCarMileage },
                {
                  key: 'readingUnit',
                  value: marketCode === 'NO' ? 'Kilometer' : 'ScandinavianMile',
                },
              ]
            : []),
          { key: 'message', value: message || '' },
        ],
      };

      try {
        const baseUrl = new URL(apiUrl);
        const leadUrl = `${baseUrl.origin}/lead`;
        const response = await fetch(leadUrl, {
          method: 'POST',
          body: JSON.stringify(payload),
          headers: {
            'Content-Type': 'application/json; charset=UTF-8',
          },
        });

        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }

        const data: LeadResponse = await response.json();
        setResponseData(data);
        return data;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Something went wrong';
        setError(errorMessage);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [apiUrl, marketCode]
  );

  return { isLoading, error, responseData, submitLead };
};

export default useSubmitLead;
